import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getPlaceInfo } from 'tripPlanner/api'
import type { Highlight } from '../components/TripPlanCanvas'

export function useSortedHighlights(highlights: Array<Highlight> | undefined) {
  const highlightsWithPlaceIds = useMemo(
    () => highlights?.filter((h): h is Highlight & { googlePlaceId: string } => !!h.googlePlaceId) ?? [],
    [highlights],
  )

  const queries = useQueries({
    queries: highlightsWithPlaceIds.map((highlight) => ({
      queryKey: ['placeInfo', highlight.googlePlaceId],
      queryFn: () => getPlaceInfo(highlight.googlePlaceId),
      staleTime: 10 * 60 * 1000,
      retry: 1,
    })),
  })

  const sortedHighlights = useMemo(() => {
    if (!highlights?.length) return []

    const fallback = highlights.slice(0, 4)

    const hasErrors = queries.some((q) => q.isError)
    if (hasErrors) return fallback

    try {
      const ratingsMap = new Map(
        highlightsWithPlaceIds
          .map((h, i) => [h.googlePlaceId, queries[i]?.data] as const)
          .filter(([, data]) => data),
      )

      if (ratingsMap.size === 0) return fallback

      return [...highlights]
        .sort((a, b) => {
          const aInfo = a.googlePlaceId ? ratingsMap.get(a.googlePlaceId) : undefined
          const bInfo = b.googlePlaceId ? ratingsMap.get(b.googlePlaceId) : undefined
          const aRating = aInfo?.rating
          const bRating = bInfo?.rating

          if (aRating !== undefined && bRating !== undefined) {
            if (aRating !== bRating) return bRating - aRating
            return (bInfo?.userRatingCount ?? 0) - (aInfo?.userRatingCount ?? 0)
          }
          if (aRating !== undefined) return -1
          if (bRating !== undefined) return 1
          return 0
        })
        .slice(0, 4)
    } catch {
      return fallback
    }
  }, [highlights, highlightsWithPlaceIds, queries])

  return { sortedHighlights }
}
